import type { ClientMsg, Role, ServerMsg } from '../src/server/protocol.ts';

export interface Client {
  send(msg: ClientMsg): void;
  /** Estimated server time in ms since epoch. */
  now(): number;
  /** Tell the server this revision is on screen (throttled). */
  seen(rev: number): void;
  /** Report a client-side failure to the back office (throttled). */
  report(message: string): void;
  close(): void;
}

export interface ClientOpts {
  role: Role;
  token?: () => string | undefined;
  key?: () => string | undefined;
  /** Back-office monitor (ops iframe): never a member, never counted as a screen. */
  preview?: boolean;
  /** Preview phone: the department whose view it shows. */
  dept?: string;
  onMessage: (msg: ServerMsg) => void;
  onState?: (connected: boolean) => void;
}

/** The server sends a heartbeat every few seconds; this much silence means the socket is dead even if it looks open. */
const SILENCE_MS = 10000;

export function connect(opts: ClientOpts): Client {
  let ws: WebSocket | null = null;
  let offset = 0;
  let attempt = 0;
  let closed = false;
  let heardAt = Date.now();
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let seenTimer: ReturnType<typeof setTimeout> | undefined;
  let seenRev = -1;
  let sentSeen = -1;
  let reports = 0;

  const raw = (msg: ClientMsg): boolean => {
    if (ws && ws.readyState === WebSocket.OPEN) { ws.send(JSON.stringify(msg)); return true; }
    return false;
  };

  /** Drop the current socket (whatever state it is in) and open a fresh one. */
  const restart = (delay: number): void => {
    clearTimeout(retryTimer);
    const old = ws; ws = null;
    if (old) { old.onopen = old.onmessage = old.onclose = old.onerror = null; try { old.close(); } catch { /* ignore */ } }
    opts.onState?.(false);
    if (!closed) retryTimer = setTimeout(open, delay);
  };

  const open = (): void => {
    if (closed) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    let sock: WebSocket;
    try { sock = new WebSocket(`${proto}//${location.host}/ws`); } catch { restart(1000); return; }
    ws = sock;
    heardAt = Date.now();
    sock.onopen = () => {
      attempt = 0; heardAt = Date.now(); sentSeen = -1;
      opts.onState?.(true);
      raw({ t: 'hello', role: opts.role, token: opts.token?.(), key: opts.key?.(), ...(opts.preview ? { preview: true, ...(opts.dept ? { dept: opts.dept } : {}) } : {}) } as ClientMsg);
    };
    sock.onmessage = (ev) => {
      heardAt = Date.now();
      let msg: ServerMsg;
      try { msg = JSON.parse(String(ev.data)) as ServerMsg; } catch { return; }
      if ('serverNow' in msg && typeof msg.serverNow === 'string') {
        const t = Date.parse(msg.serverNow);
        if (!Number.isNaN(t)) offset = t - Date.now();
      }
      if (msg.t === 'reload') { location.reload(); return; }
      if (msg.t === 'hb') return;
      try { opts.onMessage(msg); } catch (e) { report(`onMessage: ${(e as Error).message}`); }
    };
    sock.onclose = () => {
      if (ws !== sock) return;
      const delay = Math.min(5000, 300 * 2 ** attempt) + Math.random() * 300;
      attempt++;
      restart(delay);
    };
    sock.onerror = () => { try { sock.close(); } catch { /* ignore */ } };
  };

  const report = (message: string): void => {
    if (++reports > 20) return;
    raw({ t: 'clientError', message: message.slice(0, 200) });
  };

  // Liveness: a half-open socket (phone woke from sleep, Wi-Fi roam, proxy idle cut) never fires onclose.
  const watchdog = setInterval(() => {
    if (closed) return;
    if (ws && ws.readyState === WebSocket.OPEN && Date.now() - heardAt > SILENCE_MS) restart(0);
    else if (ws && ws.readyState === WebSocket.CONNECTING && Date.now() - heardAt > SILENCE_MS) restart(500);
  }, 2000);
  const check = (): void => {
    if (closed) return;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) { restart(0); return; }
    if (ws.readyState === WebSocket.OPEN) { raw({ t: 'ping' }); if (Date.now() - heardAt > 4000) setTimeout(() => { if (Date.now() - heardAt > 4000) restart(0); }, 2500); }
  };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
  window.addEventListener('online', check);
  window.addEventListener('pageshow', check);

  window.addEventListener('error', (e) => /ResizeObserver loop/.test(e.message ?? '') || report(`${e.message} @${(e.filename ?? '').split('/').pop()}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => report(`rejection: ${String((e as PromiseRejectionEvent).reason).slice(0, 160)}`));

  open();
  return {
    send: (m) => { raw(m); },
    now: () => Date.now() + offset,
    seen(rev: number) {
      seenRev = Math.max(seenRev, rev);
      if (seenTimer) return;
      seenTimer = setTimeout(() => { seenTimer = undefined; if (seenRev !== sentSeen && raw({ t: 'seen', rev: seenRev })) sentSeen = seenRev; }, 300);
    },
    report,
    close() { closed = true; clearInterval(watchdog); clearTimeout(retryTimer); try { ws?.close(); } catch { /* ignore */ } },
  };
}

export function store(key: string, value?: string | null): string | null {
  try {
    if (value === undefined) return localStorage.getItem(key);
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
  } catch { /* storage unavailable */ }
  return value ?? null;
}

/** The operator key: from the URL once (then removed from the address bar), else remembered, else asked. */
export function operatorKey(storageKey = 'meridian-ops-key'): string {
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('key');
  if (fromUrl) {
    store(storageKey, fromUrl);
    params.delete('key');
    const q = params.toString();
    history.replaceState(null, '', location.pathname + (q ? `?${q}` : '') + location.hash);
  }
  let key = fromUrl ?? store(storageKey);
  if (!key) { key = window.prompt('Operatör anahtarı') ?? ''; if (key) store(storageKey, key); }
  return key;
}
